import { useState } from "react";

import { Loader2 } from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";


interface Persona {
  role_name: string;
  mission: string;
  principles: string;
  competencies: string;
  key_relationships: string;
  typical_questions: string[];
}

interface Props {
  boardDetails: {
    name: string;
    description: string;
  };
  personas: Persona[];
  onSubmit: (feedback: string) => void;
  isSubmitting: boolean;
}

const PersonasReviewStep = ({ boardDetails, personas, onSubmit, isSubmitting }: Props) => {
  const [feedback, setFeedback] = useState("");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 2: Review Personas</CardTitle>
        <CardDescription>
          Based on your input, we've designed a panel of expert personas. Provide feedback to refine them.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
          {personas.map((persona, index) => (
            <AccordionItem value={`item-${index}`} key={index}>
              <AccordionTrigger>{persona.role_name}</AccordionTrigger>
              <AccordionContent className="space-y-4">
                <div>
                  <h4 className="font-semibold">Mission</h4>
                  <p className="text-muted-foreground">{persona.mission}</p>
                </div>
                <div>
                  <h4 className="font-semibold">Typical Questions</h4>
                  <ul className="list-disc pl-5 text-muted-foreground">
                    {persona.typical_questions.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <div className="space-y-2">
          <label htmlFor="feedback" className="font-medium">Feedback (Optional)</label>
          <Textarea
            id="feedback"
            placeholder="e.g., 'Can we add a persona with financial expertise?', 'The marketing persona should be more focused on B2B.'"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={4}
            disabled={isSubmitting}
          />
        </div>

        <div className="flex justify-end gap-4">
          <Button
            variant="default"
            onClick={() => onSubmit(feedback)}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : feedback ? (
              "Submit Feedback & Finalize"
            ) : (
              "Looks Good, Finalize"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PersonasReviewStep;
